import React from 'react';
// Layout.js - ajustements header, logo et notifications cohérents
import { Link, useLocation } from 'react-router-dom';
import NotificationCenter from './NotificationCenter';
import {
  Bell, User, Menu, X, Truck, LayoutDashboard, RefreshCw, ShoppingCart, Package, DollarSign, FileText, Star, Tag, Factory, EyeOff, Puzzle, Settings
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './auth/AuthModal';
import { Outlet } from 'react-router-dom';
import { Tooltip } from '@mui/material';

const links = [
  { name: 'Dashboard', icon: <LayoutDashboard size={20} />, path: '/dashboard' },
  { name: 'Scraping', icon: <RefreshCw size={20} />, path: '/scraping' },
  { name: 'Achats', icon: <ShoppingCart size={20} />, path: '/achats' },
  { name: 'Commandes', icon: <Truck size={20} />, path: '/commandes' },
  { name: 'Produits', icon: <Package size={20} />, path: '/produits' },
  { name: 'Dépenses', icon: <DollarSign size={20} />, path: '/depenses' },
  { name: 'Fichiers fournisseurs', icon: <FileText size={20} />, path: '/fournisseurs-files' },
  { name: 'Produits Favoris', icon: <Star size={20} />, path: '/favoris' },
];

const settingsLinks = [
  { name: 'Statut', icon: <Tag size={20} />, path: '/statut' },
  { name: 'Fournisseurs', icon: <Factory size={20} />, path: '/fournisseurs' },
  { name: 'Produits masqués', icon: <EyeOff size={20} />, path: '/masques' },
  { name: 'Jobs', icon: <Puzzle size={20} />, path: '/jobs' },
  { name: 'Règles Repricer', icon: <Settings size={20} />, path: '/rules' },
  { name: 'Paramètres Amazon', icon: <Settings size={20} />, path: '/settings' },
];

export default function Layout() {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [isMobile, setIsMobile] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { user, signOut } = useAuth();

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 1024) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleAuthClick = () => {
    if (user) {
      signOut();
    } else {
      setIsAuthModalOpen(true);
    }
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Overlay pour mobile */}
      {isMobile && isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-30 ${isSidebarCollapsed ? 'w-20' : 'w-64'} bg-primary text-white flex flex-col justify-between border-r border-border transform transition-all duration-300 ease-in-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div>
          <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-center'} border-b border-border px-4 py-4 relative`} style={{ height: '57px' }}>
            {!isSidebarCollapsed && (
              <img
                src="/logo-trackify-white.png"
                className="h-24 w-auto"
                alt="Logo"
                style={{ maxHeight: 100, objectFit: 'contain' }}
              />
            )}
            {isSidebarCollapsed && (
              <img
                src="/logo-trackify-white.png"
                className="h-10 w-auto"
                alt="Logo"
                style={{ maxHeight: 40, objectFit: 'contain' }}
              />
            )}
            {/* Bouton pour fermer la sidebar sur mobile */}
            <button 
              className="lg:hidden text-white hover:text-secondary absolute right-4"
              onClick={() => setIsSidebarOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation principale */}
          <nav className={`flex flex-col text-sm py-4 space-y-1 ${isSidebarCollapsed ? 'items-center' : ''}`}>
            {links.map(link => {
              const isActive = location.pathname === link.path;
              const activeStyles = isActive
                ? 'bg-[#28A745] text-white font-semibold'
                : 'text-gray-300 hover:text-white';
              let iconColor = isActive ? '#fff' : '#28A745';
              return isSidebarCollapsed ? (
                <Tooltip key={link.name} title={link.name} placement="right" arrow>
                  <Link
                    to={link.path}
                    className={`flex items-center justify-center px-0 py-2.5 transition-colors text-left ${activeStyles}`}
                    onClick={() => isMobile && setIsSidebarOpen(false)}
                    style={isActive ? { width: '100%', background: '#28A745', color: '#fff', borderRadius: 0 } : { width: '100%' }}
                  >
                    <span
                      className={`text-lg flex items-center justify-center`}
                      style={{ width: 38, height: 38, color: iconColor }}
                    >
                      {typeof link.icon === 'string' ? link.icon : React.cloneElement(link.icon, { color: iconColor })}
                    </span>
                  </Link>
                </Tooltip>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`flex items-center gap-3 px-6 py-2.5 transition-colors text-left ${activeStyles}`}
                  onClick={() => isMobile && setIsSidebarOpen(false)}
                  style={isActive ? { borderRadius: 0, background: '#28A745', color: '#fff' } : {}}
                >
                  <span
                    className={`text-lg flex items-center justify-center`}
                    style={{ width: 38, height: 38, color: iconColor }}
                  >
                    {typeof link.icon === 'string' ? link.icon : React.cloneElement(link.icon, { color: iconColor })}
                  </span>
                  <span className="truncate">{link.name}</span>
                </Link>
              );
            })}

            {/* Bloc Paramètres */}
            <div className={`mt-4 border-t border-border pt-4 w-full ${isSidebarCollapsed ? 'flex flex-col items-center' : ''}`}>
              {!isSidebarCollapsed && (
                <div className="px-6 pb-2 text-xs uppercase tracking-wider text-gray-400">Paramètres</div>
              )}
              {settingsLinks.map(link => {
                const isActive = location.pathname === link.path;
                const activeStyles = isActive
                  ? 'bg-[#28A745] text-white font-semibold'
                  : 'text-gray-300 hover:text-white';
                let iconColor = isActive ? '#fff' : '#28A745';
                return isSidebarCollapsed ? (
                  <Tooltip key={link.name} title={link.name} placement="right" arrow>
                    <Link
                      to={link.path}
                      className={`flex items-center justify-center px-0 py-2.5 transition-colors text-left ${activeStyles}`}
                      onClick={() => isMobile && setIsSidebarOpen(false)}
                      style={isActive ? { width: '100%', background: '#28A745', color: '#fff', borderRadius: 0 } : { width: '100%' }}
                    >
                      <span
                        className={`text-base flex items-center justify-center`}
                        style={{ width: 38, height: 38, color: iconColor }}
                      >
                        {typeof link.icon === 'string' ? link.icon : React.cloneElement(link.icon, { color: iconColor })}
                      </span>
                    </Link>
                  </Tooltip>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`flex items-center gap-3 px-6 py-2.5 transition-colors text-left ${activeStyles}`}
                    onClick={() => isMobile && setIsSidebarOpen(false)}
                    style={isActive ? { borderRadius: 0, background: '#28A745', color: '#fff' } : {}}
                  >
                    <span
                      className={`text-base flex items-center justify-center`}
                      style={{ width: 38, height: 38, color: iconColor }}
                    >
                      {typeof link.icon === 'string' ? link.icon : React.cloneElement(link.icon, { color: iconColor })}
                    </span>
                    <span className="truncate">{link.name}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Footer avec version */}
        <div className={`text-xs text-gray-300 border-t border-border px-4 py-3 ${isSidebarCollapsed ? 'text-center' : ''}`}>
          <div className="text-center">
            <span>Version 1.0.0</span>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden pt-14">
        {/* Header */}
        <header className={`h-14 bg-primary text-white shadow flex items-center justify-between px-6 border-b border-border fixed left-0 ${isSidebarCollapsed ? 'lg:left-20' : 'lg:left-64'} right-0 top-0 z-10 transition-all duration-300`}>
          <div className="flex items-center gap-2">
            {/* Bouton sandwich toujours tout à gauche */}
            <button 
              className="text-white hover:text-secondary"
              style={{ marginRight: '12px' }}
              onClick={() => {
                if (isMobile) {
                  setIsSidebarOpen((prev) => !prev);
                } else {
                  setIsSidebarCollapsed(prev => {
                    localStorage.setItem('sidebarCollapsed', !prev);
                    return !prev;
                  });
                }
              }}
              title={isSidebarCollapsed ? 'Déplier le menu' : 'Réduire le menu'}
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="text-lg font-semibold">FBA Trackify</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <NotificationCenter className="notification-center-right" />
            </div>
            <button 
              onClick={handleAuthClick}
              className="flex items-center gap-2 text-sm hover:text-secondary transition-colors"
            >
              <User className="w-5 h-5" /> 
              <span className="hidden sm:inline">
                {user ? 'Déconnexion' : 'Mon compte'}
              </span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6 bg-background">
          <Outlet />
        </main>
      </div>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
      />
    </div>
  );
}
